<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { AccordionContent, AccordionHeader, AccordionItem, AccordionRoot, AccordionTrigger } from 'reka-ui'
import WikiText from '@/components/wikitext/WikiText.vue'
import AnchorPageLabel from './AnchorPageLabel.vue'
import { useUserStore } from '@/authentication/user.store'
import { CONTENT_TYPES, blurContentPermissions } from '@/util/access_visibility'
import { betaOptIn } from '@/util/beta_opt_in'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { GOAL_STRUCTURE_EVENT_NAMES } from './goal_structure_event_names'
import type { StaircaseGoal, StaircaseGradeGroup } from './staircase_types'
import { useSendOnArrival } from './useSendOnArrival'
import type { Navigate } from './useSendOnArrival'

const props = withDefaults(defineProps<{
  goalStaircase?: StaircaseGradeGroup[]
  grades?: string[]
  pageUuid?: string
  isAnchorPageLabelShown?: boolean
}>(), {
  goalStaircase: () => [],
  grades: () => [],
  pageUuid: '',
  isAnchorPageLabelShown: true
})

const route = useRoute()
const { authInfo } = storeToRefs(useUserStore())
const analytics = getAnalyticsTracker().getChildContext({ curr_uuid: computed(() => props.pageUuid) })
const { followLinkThenSend } = useSendOnArrival()

const openGrade = ref<string>()

const gradeGroupsWithGoals = computed(() => props.goalStaircase.filter((gradeGroup) => gradeGroup.goals.length > 0))
const currentGoalGradeRange = computed(() => `${props.grades[0]}-${props.grades[props.grades.length - 1]}`)
const betaQuery = computed(() => betaOptIn(route.query))

function isCurrentGoalGrade(grade: string) {
  return props.grades.includes(grade) || grade === currentGoalGradeRange.value
}

function initiallyOpenGrade() {
  const staircaseGrades = gradeGroupsWithGoals.value.map((gradeGroup) => gradeGroup.grade)
  const firstGoalGrade = props.grades[0]
  if (staircaseGrades.includes(firstGoalGrade)) return firstGoalGrade
  if (staircaseGrades.includes(currentGoalGradeRange.value)) return currentGoalGradeRange.value
  return undefined
}

watch([() => props.grades, () => props.goalStaircase], () => {
  openGrade.value = initiallyOpenGrade()
}, { immediate: true })

function isAnchorPageLabelShownFor(goal: StaircaseGoal) {
  return props.isAnchorPageLabelShown && goal.super_page
}

function goalsByScoreDescending(goals: StaircaseGoal[]) {
  return [...goals].sort((first, second) => second.score - first.score)
}

function firstLineOf(text: string) {
  return text.split('\n')[0]
}

function isGoalLocked(goal: StaircaseGoal) {
  return blurContentPermissions(CONTENT_TYPES.SUPER_PAGE, goal, authInfo.value).blur
}

function clickedHeading(grade: string) {
  analytics.sendEvent(GOAL_STRUCTURE_EVENT_NAMES.CLICKED_STAIRCASE_HEADING, { heading: grade })
}

function clickedGoal(goal: StaircaseGoal, navigate: Navigate, event: MouseEvent) {
  return followLinkThenSend(navigate, event, () =>
    analytics.sendEvent(GOAL_STRUCTURE_EVENT_NAMES.CLICKED_STAIRCASE_GOAL, { uuid: goal.uuid, title: goal.title })
  )
}
</script>

<template>
  <AccordionRoot v-model="openGrade" type="single" collapsible class="mb-5 flex flex-col gap-1">
    <AccordionItem
      v-for="gradeGroup in gradeGroupsWithGoals"
      :key="gradeGroup.grade"
      :value="gradeGroup.grade"
      class="rounded-xs border border-gray-300 bg-white shadow-sm"
      :data-testid="`staircase-grade-${gradeGroup.grade}`"
    >
      <AccordionHeader as="h3" class="m-0 text-md font-medium leading-none">
        <AccordionTrigger
          class="w-full rounded-t-xxs px-4 py-2.5 text-left leading-5 text-blue-400 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400"
          :class="{ 'bg-gray-100': isCurrentGoalGrade(gradeGroup.grade) }"
          @click="clickedHeading(gradeGroup.grade)"
        >
          Grade {{ gradeGroup.grade }}
        </AccordionTrigger>
      </AccordionHeader>
      <AccordionContent class="border-t border-gray-300 p-4">
        <ul :data-testid="`staircase-goals-${gradeGroup.grade}`">
          <li v-for="goal of goalsByScoreDescending(gradeGroup.goals)" :key="goal.uuid">
            <h4 class="mb-1 mt-2.5 text-xl font-medium leading-none">
              <router-link
                v-slot="{ href, navigate, isExactActive }"
                :to="{ name: 'AnchorPage', params: { uuid: goal.uuid }, query: betaQuery }"
                custom
              >
                <a
                  :href="href"
                  :aria-current="isExactActive ? 'page' : undefined"
                  class="text-blue-400 hover:text-blue-500 hover:underline"
                  @click="clickedGoal(goal, navigate, $event)"
                >{{ goal.title }}</a>
              </router-link>
            </h4>
            <p v-if="isAnchorPageLabelShownFor(goal)" class="mb-2.5">
              <AnchorPageLabel />
            </p>
            <WikiText :text="firstLineOf(goal.goal_text)" plaintext :locked="isGoalLocked(goal)" />
          </li>
        </ul>
      </AccordionContent>
    </AccordionItem>
  </AccordionRoot>
</template>
